import { useQuery } from '@tanstack/react-query'
import { useAccessToken } from '@salesforce/commerce-sdk-react'
import { useAppOrigin } from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const localeOrder = ['en_CA', 'fr_CA', 'en_US', 'en_GB']

const safeParseJson = (value, fallback = {}) => {
    if (value == null || value === '') return fallback
    if (typeof value === 'object') return value
    if (typeof value !== 'string') return fallback
    try {
        return JSON.parse(value)
    } catch (error) {
        return fallback
    }
}

const sortCountries = (countries) => {
    if (!countries) return null
    return Object.fromEntries(
        Object.entries(countries).sort(([a], [b]) => {
            const indexA = localeOrder.indexOf(a)
            const indexB = localeOrder.indexOf(b)
            if (indexA === -1 && indexB === -1) return 0
            if (indexA === -1) return 1
            if (indexB === -1) return -1
            return indexA - indexB
        })
    )
}

const fetchCountrySelectorPages = async (url, token) => {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    })

    if (!response.ok) {
        throw new Error('Failed to fetch country selector pages')
    }

    return response.json()
}

const useCountrySelector = () => {
    const appOrigin = useAppOrigin()
    const { getTokenWhenReady } = useAccessToken()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['countrySelectorPages'],
        queryFn: async () => {
            const config = getConfig()
            const token = await getTokenWhenReady()
            const { proxyPath, parameters } = config.app.commerceAPI
            const organizationId = parameters.organizationId
            const siteId = parameters.siteId
            // Combine both preferences into a single API call
            const preferencesURL = `${appOrigin}${proxyPath}/custom/preferences/v1/organizations/${organizationId}/preferences?siteId=${siteId}&c_ids=countrySelectorPages,showCountrySelectorOnFooter&c_org_ids=countrySelectorConfig`

            const response = await fetchCountrySelectorPages(preferencesURL, token)

            const countrySelectorConfig = safeParseJson(response?.countrySelectorConfig, null)
            const countrySelectorPages = safeParseJson(response?.countrySelectorPages, {})

            return {
                countrySelectorConfig: sortCountries(countrySelectorConfig),
                countrySelectorPages,
                showCountrySelectorOnFooter: response?.showCountrySelectorOnFooter || false
            }
        },
        // Placement is needed on every route, even when PDP/PLP country URLs
        // are already available from product or category data.
        enabled: true,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    })

    return {
        countrySelectorData: data,
        isLoading,
        error,
        refetch
    }
}

export { useCountrySelector, sortCountries }
